import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import type { MotionValue } from 'framer-motion';
import type { LobbyRound } from '@/api/lobby.types';
import i18n from '@/i18n';

interface TopNavProps {
  rounds: LobbyRound[];
  selectedRoundId: string | null;
  onSelect: (roundId: string) => void;
  /** Passed from parent's useNow() to drive periodic re-renders. The actual time
   * comparison always uses new Date() so stale values never cause incorrect state. */
  now: number;
  dragX: MotionValue<number>;
}

function isRoundOpen(round: LobbyRound, nowIso: string): boolean {
  return round.revealDays.some(d => d.revealStart <= nowIso);
}

function formatRoundDateRange(round: LobbyRound): string {
  if (round.revealDays.length === 0) return round.name;
  const first = new Date(round.revealDays[0].revealStart);
  const lastIdx = round.revealDays.length - 1;
  const lastInclusive = new Date(new Date(round.revealDays[lastIdx].revealEnd).getTime() - 1);
  const monthFmt = new Intl.DateTimeFormat(i18n.language, { month: 'short' });
  const firstPart = `${monthFmt.format(first)} ${first.getDate()}`;
  const lastPart =
    first.getMonth() === lastInclusive.getMonth()
      ? `${lastInclusive.getDate()}`
      : `${monthFmt.format(lastInclusive)} ${lastInclusive.getDate()}`;
  return `${firstPart} - ${lastPart}`;
}

function visibleRounds(sorted: LobbyRound[], nowIso: string): LobbyRound[] {
  const currentIdx = sorted.reduce((best, r, i) => (isRoundOpen(r, nowIso) ? i : best), -1);
  // All past rounds + current + 1 next locked (minimum 2 shown)
  const end = currentIdx === -1 ? 2 : currentIdx + 2;
  return sorted.slice(0, end);
}

type Rect = { left: number; width: number };

export default function TopNav({
  rounds,
  selectedRoundId,
  onSelect,
  now: _now,
  dragX,
}: TopNavProps) {
  const nowIso = new Date().toISOString();
  const sorted = rounds.slice().sort((a, b) => a.sequence - b.sequence);
  const visible = visibleRounds(sorted, nowIso);
  const equalWidth = visible.length <= 3;
  const navRef = useRef<HTMLElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [buttonRects, setButtonRects] = useState<Rect[]>([]);

  // Scroll selected tab to center
  useEffect(() => {
    const nav = navRef.current;
    const selected = nav?.querySelector('[data-selected="true"]') as HTMLElement | null;
    if (!nav || !selected) return;

    const target = selected.offsetLeft - nav.clientWidth / 2 + selected.offsetWidth / 2;
    const start = nav.scrollLeft;
    const delta = target - start;
    const duration = 600;
    let startTime: number | null = null;
    let rafId: number;

    const step = (ts: number) => {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      nav.scrollLeft = start + delta * ease;
      if (progress < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [selectedRoundId]);

  /*
    Measure button rects whenever tab layout changes. The buttons are observed
    rather than measured once: in the scrolling layout their width follows the
    label text, which reflows when Poppins finishes loading after first paint,
    and in both layouts it follows the nav width on rotate/resize. A one-off
    measurement left the indicator sitting to the right of the selected tab.
  */
  useLayoutEffect(() => {
    const buttons = buttonRefs.current.slice(0, visible.length);
    const measure = () => {
      const rects = buttons.map(btn =>
        btn ? { left: btn.offsetLeft, width: btn.offsetWidth } : { left: 0, width: 0 }
      );
      setButtonRects(prev =>
        prev.length === rects.length &&
        prev.every((r, i) => r.left === rects[i].left && r.width === rects[i].width)
          ? prev
          : rects
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    buttons.forEach(btn => btn && observer.observe(btn));
    return () => observer.disconnect();
  }, [visible.length, equalWidth]);

  // Determine which buttons the indicator should interpolate between
  const selectedIdx = visible.findIndex(r => r.id === selectedRoundId);
  const openVisible = visible.filter(r => isRoundOpen(r, nowIso));
  const currentOpenIdx = openVisible.findIndex(r => r.id === selectedRoundId);
  const prevOpen = currentOpenIdx > 0 ? openVisible[currentOpenIdx - 1] : null;
  const nextOpen = currentOpenIdx < openVisible.length - 1 ? openVisible[currentOpenIdx + 1] : null;
  const prevIdx = prevOpen ? visible.findIndex(r => r.id === prevOpen.id) : -1;
  const nextIdx = nextOpen ? visible.findIndex(r => r.id === nextOpen.id) : -1;

  const fallback: Rect = { left: 0, width: 0 };
  const currentRect = buttonRects[selectedIdx] ?? fallback;
  const prevRect = prevIdx >= 0 ? (buttonRects[prevIdx] ?? currentRect) : currentRect;
  const nextRect = nextIdx >= 0 ? (buttonRects[nextIdx] ?? currentRect) : currentRect;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 390;
  const indicatorLeft = useTransform(
    dragX,
    [-vw, 0, vw],
    [nextRect.left, currentRect.left, prevRect.left]
  );
  const indicatorWidth = useTransform(
    dragX,
    [-vw, 0, vw],
    [nextRect.width, currentRect.width, prevRect.width]
  );

  return (
    <nav
      ref={navRef}
      className={`sticky top-0 w-full h-[40px] bg-[#222429] flex items-end z-10 ${
        equalWidth ? '' : 'overflow-x-auto px-4 gap-4'
      }`}
    >
      {visible.map((round, index) => {
        const open = isRoundOpen(round, nowIso);
        const isSelected = selectedRoundId === round.id;
        return (
          <button
            key={round.id}
            ref={el => {
              buttonRefs.current[index] = el;
            }}
            type="button"
            disabled={!open}
            data-selected={isSelected}
            onClick={() => open && onSelect(round.id)}
            className={`${equalWidth ? 'flex-1' : 'shrink-0'} flex flex-col items-center p-0 pb-[3px] ${open ? '' : 'cursor-not-allowed'}`}
          >
            <span
              className={`flex items-center gap-1 text-[12px] font-bold leading-[18px] tracking-[0.01em] text-center pb-2 px-1 uppercase ${
                isSelected ? 'text-white' : open ? 'text-white/50' : 'text-[#2F3F51]'
              }`}
            >
              {!open && (
                <img
                  src="/lock.svg"
                  alt={i18n.t('nav.locked')}
                  className="w-[10px] h-[12px] inline-block align-middle -ml-0.5"
                />
              )}
              {formatRoundDateRange(round)}
            </span>
          </button>
        );
      })}
      {/* Single sliding indicator — driven by dragX */}
      <motion.div
        className="absolute bottom-0 h-[3px] rounded-[2px] bg-[#E02125] pointer-events-none"
        style={{ left: indicatorLeft, width: indicatorWidth }}
      />
    </nav>
  );
}
